import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  CardWrapper,
  CardBody,
  CardGroup,
  CardSubtitle,
} from "@cdkglobal/radial";
import { RowData, RowDataReducerAction, RowDataReducerState } from "../../../../../types";
import { useUIStateContext } from "../../../../../contexts/UIStateContext";
import { useUserContext } from "../../../../../contexts/UserContext";
import { useRefetchRowDataList } from "../../../../../hooks";
import { getInventoryBuildingIdentity } from "./utils/inventoryBuildingPriorityUtils";
import { reorderInventoryBuildingPriorities } from "./utils/reorderInventoryBuildingPriorities";
import { SortableBuildingCard } from "./SortableBuildingCard";
import { BuildingCard } from "./BuildingCard";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  padding: 16px;
  height: calc(100vh - 12rem);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  align-content: start;
`;

interface InventoryBuildingCardsProps {
  rowDataReducerState: RowDataReducerState;
  dataToDisplay: RowData[];
  handleEdit: (rowData: RowData) => void;
  dispatch: React.Dispatch<RowDataReducerAction>;
}

export default function InventoryBuildingCards({
  rowDataReducerState,
  dataToDisplay,
  handleEdit,
  dispatch,
}: Readonly<InventoryBuildingCardsProps>): JSX.Element {
  const { rowDataList } = rowDataReducerState;
  const { setupBaseRequestInfo, headerLabel, setTableTitleInfo, permissions } =
    useUserContext();
  const { setLandingPageToast } = useUIStateContext();
  const { refetch: refetchRowDataList } = useRefetchRowDataList({
    setupBaseRequestInfo,
    headerLabel,
    dispatch,
    setTableTitleInfo,
  });

  const [orderedBuildings, setOrderedBuildings] =
    useState<RowData[]>(dataToDisplay);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const suppressEditClickUntilRef = useRef(0);
  const isReorderingRef = useRef(false);

  useEffect(() => {
    if (!isReorderingRef.current) {
      setOrderedBuildings(dataToDisplay);
    }
  }, [dataToDisplay]);

  const isReorderEnabled = permissions.setupPermission.hasManage;

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const sortableItems = useMemo(
    () =>
      orderedBuildings.map((building, index) =>
        getInventoryBuildingIdentity(building, index),
      ),
    [orderedBuildings],
  );

  const handleDragStart = ({ active }: DragStartEvent): void => {
    setActiveDragId(String(active.id));
  };

  const handleDragCancel = (): void => {
    setActiveDragId(null);
  };

  const handleDragEnd = async ({
    active,
    over,
  }: DragEndEvent): Promise<void> => {
    setActiveDragId(null);

    if (!over) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) {
      return;
    }

    const sourceIndex = orderedBuildings.findIndex(
      (building, index) =>
        getInventoryBuildingIdentity(building, index) === activeId,
    );

    const targetIndex = orderedBuildings.findIndex(
      (building, index) =>
        getInventoryBuildingIdentity(building, index) === overId,
    );

    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }

    const reorderedBuildings = arrayMove(
      orderedBuildings,
      sourceIndex,
      targetIndex,
    );

    setOrderedBuildings(reorderedBuildings);
    // Ignore only the synthetic click that can fire right after a drag-drop.
    suppressEditClickUntilRef.current = Date.now() + 150;
    isReorderingRef.current = true;

    await reorderInventoryBuildingPriorities({
      rowDataList,
      reorderedRows: reorderedBuildings,
      setupBaseRequestInfo,
      dispatch,
      setLandingPageToast,
      refetchRowDataList,
    });
    isReorderingRef.current = false;
  };

  const activeBuildingIndex =
    activeDragId == null
      ? -1
      : orderedBuildings.findIndex(
          (building, index) =>
            getInventoryBuildingIdentity(building, index) === activeDragId,
        );

  const activeBuilding =
    activeBuildingIndex >= 0 ? orderedBuildings[activeBuildingIndex] : null;

  if (!dataToDisplay.length) {
    return (
      <CardWrapper style={{ margin: "1rem" }}>
        <CardBody>
          <CardGroup>
            <CardSubtitle>No buildings to display.</CardSubtitle>
          </CardGroup>
        </CardBody>
      </CardWrapper>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={(event) => {
        void handleDragEnd(event);
      }}
    >
      <SortableContext items={sortableItems} strategy={rectSortingStrategy}>
        <Grid>
          {orderedBuildings.map((building: RowData, i: number) => {
            const buildingId = getInventoryBuildingIdentity(building, i);
            const rowIndex = dataToDisplay.findIndex(
              (record, originalIndex) =>
                getInventoryBuildingIdentity(record, originalIndex) ===
                buildingId,
            );

            return (
              <SortableBuildingCard
                key={buildingId}
                building={building}
                index={i}
                onEdit={() => {
                  if (Date.now() < suppressEditClickUntilRef.current) {
                    return;
                  }

                  if (rowIndex >= 0) {
                    handleEdit(building);
                  }
                }}
                dispatch={dispatch}
                isReorderEnabled={isReorderEnabled}
              />
            );
          })}
        </Grid>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeBuilding ? (
          <BuildingCard
            building={activeBuilding}
            index={activeBuildingIndex}
            onEdit={() => undefined}
            dispatch={dispatch}
            isDragging={true}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
